import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveDialogContent } from "@/components/ui/responsive-dialog-content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/ui/form-error";
import { Banknote, Check, Loader2 } from "lucide-react";

interface CashEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (amount: number) => void;
  onSkip?: () => void;
  onCancel?: () => void;
  title: string;
  description: string;
  submitText?: string;
  cancelText?: string;
  isMandatory?: boolean;
  hideSkipButton?: boolean;
  initialValue?: number | null;
  isLoading?: boolean;
}

export const CashEntryModal: React.FC<CashEntryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
  onCancel,
  title,
  description,
  submitText = "Enregistrer",
  cancelText = "Annuler",
  isMandatory = false,
  hideSkipButton = false,
  initialValue,
  isLoading = false,
}) => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (initialValue !== undefined && initialValue !== null) {
        const numValue =
          typeof initialValue === "string"
            ? parseFloat(initialValue)
            : Number(initialValue);
        if (!isNaN(numValue)) {
          setAmount(numValue.toFixed(2));
        } else {
          setAmount("");
        }
      } else {
        setAmount("");
      }
      setError("");
    }
  }, [isOpen, initialValue]);

  const handleSubmit = () => {
    const numericAmount = parseFloat(amount.replace(",", "."));
    if (isNaN(numericAmount)) {
      setError("Veuillez entrer un montant valide.");
      return;
    }
    if (numericAmount < 0) {
      setError("Le montant ne peut pas être négatif.");
      return;
    }
    setError("");
    onSubmit(numericAmount);
    setAmount("");
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isMandatory) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent
        onInteractOutside={isMandatory ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={isMandatory ? (e) => e.preventDefault() : undefined}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 md:px-6 py-4 border-b flex-shrink-0">
          <Banknote
            className="h-6 w-6 md:h-8 md:w-8 text-blue-600 flex-shrink-0"
            aria-hidden
          />
          <DialogTitle className="text-xl md:text-3xl font-bold">{title}</DialogTitle>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-4">
          <p className="text-sm md:text-base text-muted-foreground">
            {description}
          </p>
          <div>
            <Label htmlFor="cash-amount" className="text-base font-semibold mb-2 block">
              Montant (€)
            </Label>
            <Input
              id="cash-amount"
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError("");
              }}
              placeholder="0,00"
              min="0"
              step="0.01"
              className="h-14 text-2xl text-center font-bold"
              aria-describedby={error ? "cash-amount-error" : undefined}
              aria-invalid={error ? true : undefined}
              autoFocus
            />
            <FieldError id="cash-amount-error" message={error} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-4 md:px-6 py-4 border-t flex-shrink-0">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1 h-14 text-base"
            >
              {cancelText}
            </Button>
          )}
          {!hideSkipButton && onSkip && (
            <Button
              variant="outline"
              onClick={onSkip}
              className="flex-1 h-14 text-base"
            >
              Passer
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={
              !amount || parseFloat(amount.replace(",", ".")) < 0 || isLoading
            }
            className="flex-1 h-14 text-base"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" aria-hidden />
            ) : (
              <Check className="h-5 w-5 mr-2" aria-hidden />
            )}
            {submitText}
          </Button>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
};
